import { criterion, fail, fromCriteria, pass } from "@/core/check";
import type { Lesson } from "@/core/types";
import { echteVerbuende, readFormats } from "@/office/excel/formate";
import { readWorkbook } from "@/office/excel/workbook";
import { nichtPruefbar } from "../schnittstelle";

export const lektionLayout: Lesson = {
  id: "layout",
  title: "Spaltenbreite, Umbruch – und die Sache mit dem Verbinden",
  summary: "Warum verbundene Zellen hübsch aussehen und beim Sortieren alles zerlegen.",
  durationMinutes: 10,
  learningGoals: [
    "Spaltenbreiten und Zeilenumbruch sinnvoll einsetzen",
    "Verbundene Zellen vermeiden und ersetzen",
    "„Über Auswahl zentrieren“ als verlustfreie Alternative kennen",
  ],
  steps: [
    {
      id: "breite-und-umbruch",
      title: "Breite, Umbruch und die Raute",
      explanation: [
        "Ein Doppelklick auf die Trennlinie zwischen zwei Spaltenköpfen passt die " +
          "Spalte an ihren breitesten Inhalt an. Markierst du vorher mehrere Spalten, " +
          "gilt das für alle auf einmal – der schnellste Weg, eine importierte Datei " +
          "lesbar zu machen.",
        "Bei langen Texten ist Verbreitern aber die schlechtere Lösung. Dafür gibt es " +
          "den Zeilenumbruch unter „Start“ → „Textumbruch“: Der Text bricht innerhalb " +
          "der Zelle um, die Zeile wird höher, die Spalte bleibt schmal. Innerhalb " +
          "einer Zelle erzwingt Alt + Eingabe einen Umbruch an genau der Stelle, an der " +
          "der Cursor steht.",
        "Zeigt eine Zelle mit einer Zahl lauter Rauten, ist das kein Fehler: Excel " +
          "weigert sich, eine Zahl abgeschnitten darzustellen, weil eine abgeschnittene " +
          "Zahl gefährlich falsch aussähe. Bei Text schneidet Excel dagegen einfach ab " +
          "oder lässt ihn in die Nachbarzelle überlaufen – der Unterschied hat also " +
          "einen guten Grund.",
      ],
      keyPoints: [
        "Doppelklick auf die Spaltentrennlinie passt die Breite an den Inhalt an.",
        "Start → Textumbruch bricht langen Text in der Zelle um.",
        "Alt + Eingabe setzt einen Umbruch innerhalb der Zelle.",
        "Rauten bei Zahlen: die Spalte ist zu schmal, der Wert ist in Ordnung.",
      ],
    },
    {
      id: "verbundene-zellen",
      title: "Verbundene Zellen: der schöne Fehler",
      explanation: [
        "„Verbinden und zentrieren“ ist die verlockendste Schaltfläche in Excel. Eine " +
          "Überschrift über drei Spalten sieht damit sofort ordentlich aus. Und es ist " +
          "die Einstellung, die in fremden Tabellen die meisten Probleme verursacht.",
        "Verbundene Zellen brechen die Rasterlogik, auf der Excel aufbaut. Sortieren " +
          "über einen Bereich mit verbundenen Zellen verweigert Excel schlicht. " +
          "Filtern erfasst nur die erste Zeile eines Verbunds. Eine Spalte lässt sich " +
          "nicht mehr am Stück markieren. Und eine PivotTable über solche Daten " +
          "verweigert den Dienst, weil ihr die Spaltenüberschriften fehlen.",
        "Für den einen Fall, in dem es wirklich nur um Optik geht – eine Überschrift " +
          "mittig über mehreren Spalten –, gibt es eine verlustfreie Alternative: " +
          "„Über Auswahl zentrieren“. Zu finden mit Strg + 1 unter „Ausrichtung“ im " +
          "Klappfeld „Horizontal“. Der Text steht optisch mittig über dem Bereich, die " +
          "Zellen bleiben aber einzeln – Sortieren, Filtern und alles Weitere " +
          "funktionieren unverändert.",
        "In einer Tabelle im Sinne des vorigen Moduls stellt sich die Frage übrigens " +
          "gar nicht: Excel lässt in einer Tabelle keine verbundenen Zellen zu. Auch " +
          "das ist ein Argument für Tabellen.",
      ],
      keyPoints: [
        "Verbundene Zellen blockieren Sortieren, Filtern und PivotTables.",
        "Strg + 1 → Ausrichtung → Horizontal → „Über Auswahl zentrieren“ als Ersatz.",
        "In einer Tabelle sind verbundene Zellen gar nicht erst möglich.",
      ],
      task: {
        instruction:
          "Sorge dafür, dass in deinen Datenbereichen keine verbundenen Zellen mehr " +
          "stehen. Brauchst du eine mittige Überschrift, nimm „Über Auswahl zentrieren“.",
        hint: "Verbund auflösen: Zellen markieren, Start → „Verbinden und zentrieren“ erneut anklicken.",
        check: async () => {
          const formate = await readFormats();
          if (formate.mergedAreas === null) {
            return nichtPruefbar(
              "Verbundene Zellen eines Bereichs",
              "1.13",
              "Markiere deinen Datenbereich und sieh nach, ob die Schaltfläche „Verbinden und zentrieren“ " +
                "unter „Start“ hervorgehoben ist – dann steckt darin ein Verbund.",
              true,
            );
          }

          const verbuende = echteVerbuende(formate);
          if (verbuende.length === 0) {
            return pass(
              "Keine verbundenen Zellen gefunden – Sortieren, Filtern und PivotTables können " +
                "über deine Daten hinweg arbeiten.",
            );
          }

          const erster = verbuende[0];
          return fail(
            `Auf „${erster.sheetName}“ ist der Bereich ${erster.address} verbunden` +
              `${verbuende.length > 1 ? ` – zusammen mit ${verbuende.length - 1} weiteren Stellen` : ""}.`,
            "Markiere den Bereich und klicke „Verbinden und zentrieren“ erneut an, um den Verbund " +
              "aufzulösen. Für eine mittige Überschrift nimm stattdessen Strg + 1 → Ausrichtung → " +
              "Horizontal → „Über Auswahl zentrieren“.",
          );
        },
      },
    },
    {
      id: "ausrichtung",
      title: "Ausrichtung sagt etwas aus",
      explanation: [
        "Die Standardausrichtung – Zahlen rechts, Text links – ist kein Zufall und " +
          "sollte nicht ohne Grund geändert werden. Sie ist die Statusanzeige aus " +
          "Modul 2: Wer alle Zellen zentriert, macht diese Anzeige unbrauchbar und " +
          "kann eine als Text gespeicherte Zahl nicht mehr auf einen Blick erkennen.",
        "Zahlen gehören außerdem aus einem zweiten Grund rechtsbündig: Nur so stehen " +
          "die Stellen untereinander. In einer zentrierten Spalte lassen sich " +
          "Größenordnungen nicht mehr vergleichen, weil 9,50 und 1.250,00 " +
          "unterschiedlich weit ausgerückt sind.",
        "Wo Zentrieren wirklich passt, ist die Kopfzeile über schmalen Zahlenspalten " +
          "und bei kurzen Kennzeichen wie „ja“/„nein“ oder Kürzeln. Und für lange " +
          "Spaltenüberschriften über schmalen Spalten gibt es noch den gedrehten Text: " +
          "Strg + 1 → „Ausrichtung“ → den Zeiger im Halbkreis auf 45 oder 90 Grad " +
          "stellen.",
      ],
      keyPoints: [
        "Die Standardausrichtung ist eine Statusanzeige – nicht ohne Grund überschreiben.",
        "Rechtsbündige Zahlen stehen stellenrichtig untereinander.",
        "Strg + 1 → Ausrichtung dreht Überschriften über schmalen Spalten.",
      ],
      task: {
        instruction:
          "Prüfe deine Tabelle: Die Zahlenspalten sollen rechtsbündig stehen, also " +
          "nicht von Hand zentriert sein.",
        hint: "Spalte markieren und die Ausrichtungsschaltflächen unter „Start“ ansehen – ist eine hervorgehoben, wurde von Hand ausgerichtet.",
        check: async () => {
          const mappe = await readWorkbook();
          const formate = await readFormats();

          if (formate.cellFormats === null) {
            return nichtPruefbar(
              "Die Ausrichtung einer Zelle",
              "1.9",
              "Markiere deine Zahlenspalten und sieh nach, ob unter „Start“ eine Ausrichtungsschaltfläche hervorgehoben ist.",
            );
          }
          if (!mappe.features.cells) {
            return nichtPruefbar(
              "Der Werttyp einer Zelle",
              "1.4",
              "Sieh nach, ob deine Zahlen rechtsbündig stehen.",
            );
          }

          // Zahlenzellen, deren Ausrichtung von Hand auf zentriert oder links
          // gestellt wurde. "General" ist die Voreinstellung und damit richtig.
          const zahlen = new Map<string, true>();
          for (const blatt of mappe.sheets) {
            for (const zelle of blatt.cells) {
              if (zelle.valueType === "Double" || zelle.valueType === "Integer") {
                zahlen.set(`${blatt.name}|${zelle.address}`, true);
              }
            }
          }

          const verstellt = formate.cellFormats.filter(
            (zelle) =>
              zahlen.has(`${zelle.sheetName}|${zelle.address}`) &&
              (zelle.horizontalAlignment === "Center" || zelle.horizontalAlignment === "Left"),
          );

          return fromCriteria(
            [
              criterion(
                `Zahlen in der Arbeitsmappe vorhanden (gefunden: ${zahlen.size})`,
                zahlen.size > 0,
              ),
              criterion(
                verstellt.length === 0
                  ? "Alle Zahlenzellen stehen rechtsbündig"
                  : `Von Hand ausgerichtet: ${verstellt.slice(0, 3).map((zelle) => zelle.address).join(", ")}` +
                    `${verstellt.length > 3 ? ` und ${verstellt.length - 3} weitere` : ""}`,
                verstellt.length === 0,
              ),
            ],
            {
              success:
                "Die Zahlen stehen rechtsbündig – die Stellen liegen untereinander, und die " +
                "Ausrichtung verrät weiterhin, ob eine Zelle wirklich eine Zahl enthält.",
              failure: "Da wurden Zahlenzellen von Hand ausgerichtet.",
              hint: "Markiere die Spalte und schalte die hervorgehobene Ausrichtungsschaltfläche unter " +
                "„Start“ wieder aus. Für Tabellen ist die Voreinstellung die richtige Wahl.",
            },
          );
        },
      },
    },
  ],
};
